import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { RateTrendPoint, ReportBucket, ReportFilters, ReportGroupBy } from '../../../core/models/report.model';
import { PermissionService } from '../../../core/services/permission.service';
import { ReportsService } from '../../../core/services/reports.service';
import { TranslationService } from '../../../core/services/translation.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ReportFilterBarComponent } from '../../../shared/components/report-filter-bar/report-filter-bar.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { exportToCSV, exportToXLSX, generateFilename } from '../../../shared/utils/export.utils';

interface BucketGroupModel {
  bucket: ReportBucket;
  groupBy: ReportGroupBy;
}

/** "Metrics over time, grouped by user / team / tag" (design doc §8). */
@Component({
  selector: 'app-rate-trends',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ReportFilterBarComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
  templateUrl: './rate-trends.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./rate-trends.component.css', '../../../../css/report-print.css'],
})
export class RateTrendsComponent {
  private reportsService = inject(ReportsService);
  private permissionService = inject(PermissionService);
  private translate = inject(TranslationService);

  includeAllUsers = this.permissionService.has('interaction.read-others');
  canExport = this.permissionService.has('report.export');

  points = signal<RateTrendPoint[]>([]);
  loading = signal(false);
  private lastFilters: ReportFilters | null = null;

  bucketGroupForm = new FormGroup({});
  bucketGroupModel: BucketGroupModel = { bucket: 'day', groupBy: 'user' };
  bucketGroupFields: FormlyFieldConfig[] = [
    {
      key: 'bucket',
      type: 'native-select',
      props: {
        label: this.translate.translate('reports.rateTrends.bucket'),
        showLabel: true,
        options: [
          { value: 'day', label: this.translate.translate('reports.rateTrends.day') },
          { value: 'week', label: this.translate.translate('reports.rateTrends.week') },
          { value: 'month', label: this.translate.translate('reports.rateTrends.month') },
        ],
      },
    },
    {
      key: 'groupBy',
      type: 'native-select',
      props: {
        label: this.translate.translate('reports.rateTrends.groupBy'),
        showLabel: true,
        options: [
          { value: 'user', label: this.translate.translate('reports.rateTrends.byUser') },
          { value: 'team', label: this.translate.translate('reports.rateTrends.byTeam') },
          { value: 'tag', label: this.translate.translate('reports.rateTrends.byTag') },
        ],
      },
    },
  ];

  constructor() {
    this.bucketGroupForm.valueChanges.subscribe(() => this.reload());
  }

  async onFiltersChange(filters: ReportFilters): Promise<void> {
    this.lastFilters = filters;
    await this.reload();
  }

  private async reload(): Promise<void> {
    if (!this.lastFilters) return;
    this.loading.set(true);
    try {
      this.points.set(
        await this.reportsService.getRateTrends(this.lastFilters, this.includeAllUsers, this.bucketGroupModel.bucket, this.bucketGroupModel.groupBy),
      );
    } finally {
      this.loading.set(false);
    }
  }

  exportCsv(): void {
    exportToCSV(this.exportRows(), generateFilename('rate-trends'));
  }

  exportXlsx(): void {
    exportToXLSX(this.exportRows(), generateFilename('rate-trends'), 'Rate Trends');
  }

  private exportRows(): Record<string, string | number>[] {
    return this.points().map((p) => ({
      Date: p.bucketLabel,
      Group: p.groupLabel,
      Doors: p.doors,
      Contacts: p.contacts,
      'Contact rate': `${(p.contactRate * 100).toFixed(1)}%`,
      Leads: p.leads,
    }));
  }
}
